import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

type FieldWrapProps = {
  label: string;
  error?: string | null;
  children: ReactNode;
  hint?: string;
};

export function FieldWrap({ label, error, children, hint }: FieldWrapProps) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && !error ? <span className="muted">{hint}</span> : null}
      {error ? <span className="error">{error}</span> : null}
    </div>
  );
}

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string | null;
  hint?: string;
  dirAuto?: boolean;
};

export function TextField({ label, error, hint, dirAuto, ...rest }: TextFieldProps) {
  return (
    <FieldWrap label={label} error={error} hint={hint}>
      <input className="input" dir={dirAuto ? 'auto' : undefined} {...rest} />
    </FieldWrap>
  );
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string | null;
  hint?: string;
  dirAuto?: boolean;
};

export function TextAreaField({ label, error, hint, dirAuto, ...rest }: TextAreaProps) {
  return (
    <FieldWrap label={label} error={error} hint={hint}>
      <textarea className="textarea" dir={dirAuto ? 'auto' : undefined} {...rest} />
    </FieldWrap>
  );
}

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string | null;
  hint?: string;
  options: Array<{ value: string; label: string }>;
};

export function SelectField({ label, error, hint, options, ...rest }: SelectFieldProps) {
  return (
    <FieldWrap label={label} error={error} hint={hint}>
      <select className="select" {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldWrap>
  );
}
